import { z } from 'zod'

import { nullableNumberSchema, nullableStringSchema } from './dal-utils'

const assetDimensionSchema = z.object({
  width: z.number().optional(),
  height: z.number().optional(),
})

export const contentAssetDetailsSchema = z.object({
  image: assetDimensionSchema.optional(),
  video: assetDimensionSchema.optional(),
})

export type ContentAssetDetails = z.infer<typeof contentAssetDetailsSchema>

export const contentAssetSchema = z.object({
  id: z.string(),
  url: nullableStringSchema,
  contentType: z.string(),
  fileName: nullableStringSchema,
  details: contentAssetDetailsSchema.nullable(),
})

export type ContentAsset = z.infer<typeof contentAssetSchema>

export const contentViewSchema = z.object({
  id: z.string(),
  pageName: nullableStringSchema,
  headline: nullableStringSchema,
  subheadline: nullableStringSchema,
  textHeader: nullableStringSchema,
  textContent: nullableStringSchema,
  primaryButtonText: nullableStringSchema,
  secondaryButtonText: nullableStringSchema,
  additionalTextSections: z.unknown(),
})

export type ContentView = z.infer<typeof contentViewSchema>

export const contentSettingsPageSchema = z.object({
  id: z.string(),
  displayName: nullableStringSchema,
  header: nullableStringSchema,
  termOfServiceText: nullableStringSchema,
  termOfServiceLink: nullableStringSchema,
  privacyPolicyText: nullableStringSchema,
  privacyPolicyLink: nullableStringSchema,
})

export type ContentSettingsPage = z.infer<typeof contentSettingsPageSchema>

export const contentSubjectSummarySchema = z.object({
  id: z.string(),
  title: nullableStringSchema,
  icon: contentAssetSchema.nullable(),
})

export type ContentSubjectSummary = z.infer<typeof contentSubjectSummarySchema>

export const contentStepSchema = z.object({
  id: z.string(),
  title: nullableStringSchema,
  description: nullableStringSchema,
  media: z.array(contentAssetSchema),
  lengthSeconds: nullableNumberSchema,
})

export type ContentStep = z.infer<typeof contentStepSchema>

export const contentSessionSchema = z.object({
  id: z.string(),
  title: nullableStringSchema,
  description: nullableStringSchema,
  media: contentAssetSchema.nullable(),
  lengthMinutes: nullableNumberSchema,
  featured: z.boolean(),
  availableFree: z.boolean().nullable(),
  category: contentSubjectSummarySchema.nullable(),
  steps: z.array(contentStepSchema),
})

export type ContentSession = z.infer<typeof contentSessionSchema>

export const contentLibraryCategorySchema = z.object({
  id: z.string(),
  title: nullableStringSchema,
  sessions: z.array(contentSessionSchema),
})

export type ContentLibraryCategory = z.infer<
  typeof contentLibraryCategorySchema
>

export const dashboardContentWeekSchema = z.object({
  id: z.string(),
  week: nullableNumberSchema,
  dailyActivities: z.boolean().nullable(),
  sessions: z.array(contentSessionSchema),
})

export type DashboardContentWeek = z.infer<typeof dashboardContentWeekSchema>

export const dashboardContentSubjectSchema = z.object({
  id: z.string(),
  title: nullableStringSchema,
  programId: nullableStringSchema,
  icon: contentAssetSchema.nullable(),
  weeks: z.array(dashboardContentWeekSchema),
})

export type DashboardContentSubject = z.infer<
  typeof dashboardContentSubjectSchema
>

export const dashboardContentTipSchema = z.object({
  id: z.string(),
  day: nullableNumberSchema,
  tip: nullableStringSchema,
  picture: contentAssetSchema.nullable(),
})

export type DashboardContentTip = z.infer<typeof dashboardContentTipSchema>

export const appContentSchema = z.object({
  id: z.string(),
  name: nullableStringSchema,
  settingsPage: contentSettingsPageSchema,
  onboardingIntroStep: contentViewSchema,
  onboardingCreateProfile: contentViewSchema,
  onboardingPaymentStep: contentViewSchema,
  warmupSubject: dashboardContentSubjectSchema.nullable(),
  sessionDefinition: z.object({
    alto: z.number().int().positive(),
    baritone: z.number().int().positive(),
    bass: z.number().int().positive(),
    mezzoSoprano: z.number().int().positive(),
    soprano: z.number().int().positive(),
    tenor: z.number().int().positive(),
    unknown: z.number().int().positive(),
  }),
})

export type AppContent = z.infer<typeof appContentSchema>
